import React from "react";
import { Link as RouterLink, useLocation } from "react-router-dom";
import { Menu, MenuItem, ListItemIcon, ListItemText, useMediaQuery, useTheme } from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faBell,
  faUser,
  faLock,
  faDownload,
  faSignOutAlt,
  faBook,
  faUserPlus,
  faChalkboardTeacher,
  faInfoCircle,
  faEnvelope,
  faClipboardList,
  faArchive,
  faUserGraduate,
  faUsers,
  faChartBar,
  faPhone,
  faComment,
} from "@fortawesome/free-solid-svg-icons";
import { User } from "../models/User";
import { AlertTriangle } from "lucide-react";

interface MainHeaderProps {
  isLoggedIn: boolean;
  user: User | null;
  onUserMenuClick: (event: React.MouseEvent<HTMLElement>) => void;
  onUserMenuClose: () => void;
  userMenuAnchorEl: HTMLElement | null;
  onLogout: () => void;
  onChangePassword: () => void;
  onDownloadTemplate: () => void;
}

const MainHeader: React.FC<MainHeaderProps> = ({
  isLoggedIn,
  user,
  onUserMenuClick,
  onUserMenuClose,
  userMenuAnchorEl,
  onLogout,
  onChangePassword,
  onDownloadTemplate,
}) => {
  const location = useLocation();

  const navItems = [
    { label: "Courses", path: "/courses", icon: faBook },
    { label: "Register for Course", path: "/register", icon: faUserPlus },
    {
      label: "Online Courses",
      path: "/online-course",
      icon: faChalkboardTeacher,
    },
    { label: "Resources", path: "/resources", icon: faInfoCircle },
    { label: "Contact Us", path: "/contact", icon: faEnvelope },
  ];

  const adminNavItems = [
    {
      label: "Course Management",
      path: "/admin/courses",
      icon: faClipboardList,
    },
    {
      label: "Archived Courses",
      path: "/admin/archived-courses",
      icon: faArchive,
    },
    {
      label: "Participant Management",
      path: "/admin/participants",
      icon: faUserGraduate,
    },
    { label: "User Management", path: "/admin/users", icon: faUsers },
    { label: "Reports", path: "/admin/reports", icon: faChartBar },
  ];

  return (
    <div>
      {/* Emergency Banner */}
      <div className="midnightBlueBackground" style={{ color: "white", display: 'flex', justifyContent: 'end'}}>
        <div className="container mx-auto px-4 py-0" style={{margin: '0', display: 'flex', justifyContent: 'end'}}>
          <div className="flex items-center justify-center gap-2" style={{fontSize: "13px" }}>
            <AlertTriangle className="w-6 h-6" style={{height: "18px"}} />
            {/* Lucide for warning/alert icons */}
            <p>
              Report Emergencies, Crimes, or Suspicious Activity:
            </p>
            <div className="flex items-center gap-3">
              <span className="flex items-center">
                <FontAwesomeIcon icon={faPhone} className="mr-2" />
                <span className="font-bold">(800) 331-0008</span>
              </span>
              <span className="flex items-center">
                <FontAwesomeIcon icon={faComment} className="mr-2" />
                <span className="font-bold">APD11 (27311)</span>
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Main Navigation Area */}
      <div className="px-4 py-0 midnightBlueBackground">
        <div className="flex items-end justify-between" style={{height: "60px"}}>
          {/* Navigation Items */}
          <div className="flex items-center justify-center flex-grow mx-8">
            <RouterLink to="/">
              <img
                style={{height: "80px", marginLeft: "-100px", marginRight: "20px"}}
                src="/images/railsafe-logo.png"
                alt="RAILSAFE"
                className="h-14 object-contain"
              />
            </RouterLink>
            {navItems.map((item) => (
              <RouterLink
                key={item.label}
                to={item.path}
                style={{textDecoration: "none", marginTop: "40px"}}
                className={`
                  px-6 py-3 
                  text-white text-[18px] font-medium
                  border-b-[3px] transition-all duration-200
                  flex items-center gap-2
                  ${
                    location.pathname === item.path
                      ? "border-white amtrackBlueBackground"
                      : "border-transparent hover:border-white/50 hover:bg-white/10"
                  }
                `}
              >
                <FontAwesomeIcon icon={item.icon} className="w-4 h-4" />
                <span>{item.label}</span>
              </RouterLink>
            ))}
          </div>

          {/* User Menu */}
          <div className="flex-shrink-0">
            {isLoggedIn ? (
              <div>
                <span
                  onClick={onUserMenuClick}
                  className="text-white/80 text-sm hover:text-white cursor-pointer transition-colors flex items-center gap-2"
                  role="button"
                >
                  <FontAwesomeIcon icon={faUser} className="w-4 h-4" />
                  {user?.firstName} {user?.lastName}
                </span>
                <Menu
                  anchorEl={userMenuAnchorEl}
                  open={Boolean(userMenuAnchorEl)}
                  onClose={onUserMenuClose}
                  sx={{ zIndex: 1400 }}
                >
                  {adminNavItems.map((item) => (
                    <MenuItem
                      key={item.label}
                      component={RouterLink}
                      to={item.path}
                      onClick={onUserMenuClose}
                    >
                      <ListItemIcon>
                        <FontAwesomeIcon icon={item.icon} />
                      </ListItemIcon>
                      <ListItemText>{item.label}</ListItemText>
                    </MenuItem>
                  ))}
                  <MenuItem onClick={onDownloadTemplate}>
                    <ListItemIcon>
                      <FontAwesomeIcon icon={faDownload} />
                    </ListItemIcon>
                    <ListItemText>Download Invitation Template</ListItemText>
                  </MenuItem>
                  <MenuItem onClick={onChangePassword}>
                    <ListItemIcon>
                      <FontAwesomeIcon icon={faLock} />
                    </ListItemIcon>
                    <ListItemText>Change Password</ListItemText>
                  </MenuItem>
                  <MenuItem onClick={onLogout}>
                    <ListItemIcon>
                      <FontAwesomeIcon icon={faSignOutAlt} />
                    </ListItemIcon>
                    <ListItemText>Logout</ListItemText>
                  </MenuItem>
                </Menu>
              </div>
            ) : <div/>}
          </div>
        </div>
      </div>
    </div>
  );
};

export default MainHeader;
